import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  type CalibreFamilyEntry,
  type CalibreFamilyInspiredBy,
  type CalibreFamilyFunctionalCompetitor,
  type CustomCalibreEntry,
  type SuggestCalibreFamilyResult,
  type FunctionalCompetitorSuggestion,
  type CalibreFamilyConfidence,
  getCalibreFamiliesForManufacturer,
  getCalibresForManufacturer,
  getCalibreFamilyById,
  getCustomCalibreById,
  saveCalibreFamily,
  deleteCalibreFamily,
  setCalibreFamilyMembership,
  suggestCalibreFamily,
  onTierVocabularyChanged,
} from '../../services/firebase';
import {
  searchInspiredByRefs,
  searchManufacturerCalibres,
  type InspiredByRefHit,
  type ManufacturerCalibreHit,
} from '../../services/algolia';
import {
  gateSuggestions,
  type SuggestionState,
} from './calibreFamilySuggestionGate';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { FamilyTierNamesSection } from './FamilyTierNamesSection';
import { Badge } from '../common/Badge';

/**
 * Families panel shown on the manufacturer detail (edit mode only — needs a
 * persisted manufacturerId). Lists the manufacturer's calibre families with
 * member counts, base calibre, inspired-by hint and a description preview, and
 * hosts the family editor modal.
 *
 * Owns the manufacturer's calibre list so the editor can resolve base/member
 * calibres without re-querying; membership writes go through `reloadCalibres`.
 */
export function CalibreFamiliesSection({ manufacturerId }: { manufacturerId: string }) {
  const [families, setFamilies] = useState<CalibreFamilyEntry[]>([]);
  const [calibres, setCalibres] = useState<CustomCalibreEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState<
    { open: false } | { open: true; family: CalibreFamilyEntry | null }
  >({ open: false });

  // Tier names save immediately from inside the editor, without a reload —
  // patch the cached rows so a re-opened family shows what was written.
  useEffect(
    () =>
      onTierVocabularyChanged((kind, id, next) => {
        if (kind !== 'family') return;
        setFamilies((prev) => prev.map((f) => (f.id === id ? { ...f, tierVocabulary: next } : f)));
      }),
    [],
  );

  const reloadFamilies = useCallback(async () => {
    const fams = await getCalibreFamiliesForManufacturer(manufacturerId);
    setFamilies(fams);
    return fams;
  }, [manufacturerId]);

  const reloadCalibres = useCallback(async () => {
    const cals = await getCalibresForManufacturer(manufacturerId);
    setCalibres(cals);
    return cals;
  }, [manufacturerId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      getCalibreFamiliesForManufacturer(manufacturerId),
      getCalibresForManufacturer(manufacturerId),
    ])
      .then(([fams, cals]) => {
        if (cancelled) return;
        setFamilies(fams);
        setCalibres(cals);
      })
      .catch((err) => {
        console.error('[CalibreFamily] load failed', err);
        if (!cancelled) toast.error(`Failed to load calibre families: ${errorMessage(err)}`);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [manufacturerId]);

  // member counts + base-calibre names keyed by familyId / calibreId
  const memberCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const c of calibres) {
      if (c.familyId) counts.set(c.familyId, (counts.get(c.familyId) ?? 0) + 1);
    }
    return counts;
  }, [calibres]);

  const calibreNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of calibres) m.set(c.id, c.name);
    return m;
  }, [calibres]);

  // Display labels for each family's inspired-by reference, keyed by family id.
  // The reference persists only a structured FK (a calibre/family on another
  // manufacturer — e.g. Sellita SW200 → ETA 2824), so the human-readable name is
  // resolved on read rather than stored. Families whose reference can't be
  // resolved (missing FK or deleted target) are simply absent from the map, and
  // the badge is hidden — we never fall back to the raw discriminator.
  const [inspiredByLabels, setInspiredByLabels] = useState<Map<string, string>>(
    new Map(),
  );
  useEffect(() => {
    let cancelled = false;
    const withRef = families.filter((f) => f.inspiredBy);
    if (withRef.length === 0) {
      setInspiredByLabels(new Map());
      return;
    }
    (async () => {
      const entries = await Promise.all(
        withRef.map(async (f): Promise<readonly [string, string] | null> => {
          const ib = f.inspiredBy!;
          try {
            if (ib.type === 'family' && ib.familyId) {
              const fam = await getCalibreFamilyById(ib.familyId);
              return fam ? [f.id, fam.displayName || fam.name] : null;
            }
            if (ib.type === 'calibre' && ib.calibreId) {
              const local = calibreNameById.get(ib.calibreId);
              if (local) return [f.id, local];
              const cal = await getCustomCalibreById(ib.calibreId);
              return cal ? [f.id, cal.name] : null;
            }
          } catch (err) {
            console.error('[CalibreFamily] inspired-by label resolve failed', err);
          }
          return null;
        }),
      );
      if (cancelled) return;
      setInspiredByLabels(
        new Map(entries.filter((e): e is readonly [string, string] => e !== null)),
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [families, calibreNameById]);

  // Display labels for each family's functional competitors, keyed by family id.
  // Like inspired-by, each entry persists only a structured FK (family/calibre on
  // another manufacturer), so the human-readable names are resolved on read. A
  // family maps to the list of names that resolved; entries whose target is
  // missing/deleted are simply dropped from its list (never the raw FK).
  const [competitorLabels, setCompetitorLabels] = useState<Map<string, string[]>>(
    new Map(),
  );
  useEffect(() => {
    let cancelled = false;
    const withFC = families.filter((f) => f.functionalCompetitor?.length);
    if (withFC.length === 0) {
      setCompetitorLabels(new Map());
      return;
    }
    (async () => {
      const entries = await Promise.all(
        withFC.map(async (f): Promise<readonly [string, string[]] | null> => {
          const names = await Promise.all(
            (f.functionalCompetitor ?? []).map(async (e): Promise<string | null> => {
              try {
                if (e.type === 'family' && e.familyId) {
                  const fam = await getCalibreFamilyById(e.familyId);
                  return fam ? fam.displayName || fam.name : null;
                }
                if (e.type === 'calibre' && e.calibreId) {
                  const local = calibreNameById.get(e.calibreId);
                  if (local) return local;
                  const cal = await getCustomCalibreById(e.calibreId);
                  return cal ? cal.name : null;
                }
              } catch (err) {
                console.error('[CalibreFamily] competitor label resolve failed', err);
              }
              return null;
            }),
          );
          const resolved = names.filter((n): n is string => n !== null);
          return resolved.length ? ([f.id, resolved] as const) : null;
        }),
      );
      if (cancelled) return;
      setCompetitorLabels(
        new Map(entries.filter((e): e is readonly [string, string[]] => e !== null)),
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [families, calibreNameById]);

  const handleSaved = async (savedFamilyId?: string) => {
    const [fams] = await Promise.all([reloadFamilies(), reloadCalibres()]);
    // After a brand-new family's first save, re-point the open editor at the
    // now-persisted family. Edit mode then rests on a durable source (the
    // editor's `family` prop) rather than only the modal's local `savedId`, so
    // the create→edit transition — and the fields it unlocks — survive the
    // post-save reload re-render instead of needing a reopen.
    if (savedFamilyId && editor.open && editor.family === null) {
      const created = fams.find((f) => f.id === savedFamilyId);
      if (created) setEditor({ open: true, family: created });
    }
  };

  return (
    <div className="border-t pt-4">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Calibre Families</h3>
          <p className="text-xs text-gray-500">
            Movement-family groupings (e.g. Sellita SW200). Calibres are assigned to a
            family via the editor below.
          </p>
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setEditor({ open: true, family: null })}
        >
          + Add Family
        </Button>
      </div>

      {loading ? (
        <div className="text-sm text-gray-500 py-3">Loading families…</div>
      ) : families.length === 0 ? (
        <div className="text-sm text-gray-500 italic py-3 border border-dashed border-gray-200 rounded-lg text-center">
          No families yet for this manufacturer.
        </div>
      ) : (
        <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg overflow-hidden">
          {families.map((f) => {
            const count = memberCounts.get(f.id) ?? 0;
            const baseName = f.baseCalibreId
              ? calibreNameById.get(f.baseCalibreId) ?? f.baseCalibreId
              : null;
            const inspiredByName = f.inspiredBy ? inspiredByLabels.get(f.id) : null;
            const competitorNames = competitorLabels.get(f.id);
            return (
              <li key={f.id}>
                <button
                  type="button"
                  onClick={() => setEditor({ open: true, family: f })}
                  className="w-full text-left px-3 py-2.5 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-gray-900">{f.name}</span>
                    {f.displayName && f.displayName !== f.name && (
                      <span className="text-xs text-gray-400">({f.displayName})</span>
                    )}
                    <Badge variant={count > 0 ? 'info' : 'default'}>
                      {count} member{count === 1 ? '' : 's'}
                    </Badge>
                    {baseName && (
                      <Badge variant="default">base: {baseName}</Badge>
                    )}
                    {inspiredByName && (
                      <Badge variant="amber">
                        inspired by {inspiredByName}
                      </Badge>
                    )}
                  </div>
                  {competitorNames && competitorNames.length > 0 && (
                    <p className="text-xs text-gray-500 mt-1">
                      <span className="text-gray-400">competes with:</span>{' '}
                      {competitorNames.join(' · ')}
                    </p>
                  )}
                  {f.description && (
                    <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                      {f.description}
                    </p>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {editor.open && (
        <CalibreFamilyEditorModal
          manufacturerId={manufacturerId}
          family={editor.family}
          families={families}
          calibres={calibres}
          reloadCalibres={reloadCalibres}
          onClose={() => setEditor({ open: false })}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Editor modal
// ---------------------------------------------------------------------------

type ConfidenceBadgeVariant = 'success' | 'warning' | 'default';
function confidenceVariant(c?: CalibreFamilyConfidence): ConfidenceBadgeVariant {
  return c === 'high' ? 'success' : c === 'medium' ? 'warning' : 'default';
}

/**
 * A functional-competitor entry plus its resolved display label. The label isn't
 * persisted (it's re-resolved on read), but the editor carries it alongside the
 * FK so chips render a name rather than a raw id.
 */
interface CompetitorRow {
  entry: CalibreFamilyFunctionalCompetitor;
  label: string;
}

/** Stable identity for a competitor entry — dedupes adds across picker + AI. */
function competitorKey(e: CalibreFamilyFunctionalCompetitor): string {
  return `${e.type}:${e.familyId ?? e.calibreId ?? ''}`;
}

/**
 * Best-effort human-readable cause from a thrown value. Firestore rejects with a
 * `FirebaseError` carrying a `code` (e.g. `permission-denied`) and a `message`
 * (e.g. "Missing or insufficient permissions."); surface both so a toast names
 * the real backend cause instead of a generic failure string.
 */
function errorMessage(err: unknown): string {
  if (err instanceof Error) {
    const code = (err as { code?: string }).code;
    return code && !err.message.includes(code) ? `${err.message} (${code})` : err.message;
  }
  if (typeof err === 'string') return err;
  return 'Unknown error';
}

/**
 * A one-shot search seed for a typeahead picker. `nonce` lets the same `query`
 * be pushed twice in a row (e.g. re-applying the same AI hint) and still trigger
 * the picker's effect.
 */
interface Prefill {
  query: string;
  nonce: number;
}

function CalibreFamilyEditorModal({
  manufacturerId,
  family,
  families,
  calibres,
  reloadCalibres,
  onClose,
  onSaved,
}: {
  manufacturerId: string;
  family: CalibreFamilyEntry | null;
  /** Every family of this manufacturer — the tier-names promote prompt looks across them. */
  families: CalibreFamilyEntry[];
  calibres: CustomCalibreEntry[];
  reloadCalibres: () => Promise<CustomCalibreEntry[]>;
  onClose: () => void;
  onSaved: (savedFamilyId?: string) => Promise<void> | void;
}) {
  // The id we're editing — promotes from null → real id after the first save of
  // a new family, so members can be added without reopening the modal.
  const [savedId, setSavedId] = useState<string | null>(family?.id ?? null);
  const isEdit = savedId !== null;

  const [name, setName] = useState(family?.name ?? '');
  const [displayName, setDisplayName] = useState(family?.displayName ?? '');
  const [description, setDescription] = useState(family?.description ?? '');
  const [baseCalibreId, setBaseCalibreId] = useState(family?.baseCalibreId ?? '');
  const [baseCalibreLabel, setBaseCalibreLabel] = useState<string>('');
  const [inspiredBy, setInspiredBy] = useState<CalibreFamilyInspiredBy | null>(
    family?.inspiredBy ?? null,
  );
  const [inspiredByLabel, setInspiredByLabel] = useState<string>('');
  // Functional competitors — an array of structured FKs with resolved labels.
  const [competitors, setCompetitors] = useState<CompetitorRow[]>(() =>
    (family?.functionalCompetitor ?? []).map((entry) => ({ entry, label: '' })),
  );
  // Once revealed (it has content, or AI suggested some, or the user opened it),
  // the competitors section stays visible so an old/empty family stays clean
  // until there's a reason to show it.
  const [competitorsRevealed, setCompetitorsRevealed] = useState(false);

  // Canonical tier-name vocabulary. Member calibres draw tier names from this
  // list (via the calibre tier editor's combobox + did-you-mean) to prevent
  // drift. Persisted with the family on Save.
  const [tierNames, setTierNames] = useState<string[]>(family?.tierNames ?? []);
  const [newTierName, setNewTierName] = useState('');
  const sortedTierNames = useMemo(
    () => [...tierNames].sort((a, b) => a.localeCompare(b)),
    [tierNames],
  );
  const addTierName = () => {
    const t = newTierName.trim();
    if (!t) return;
    if (tierNames.some((n) => n.toLowerCase() === t.toLowerCase())) {
      toast.error(`"${t}" is already in the vocabulary`);
      return;
    }
    setTierNames((prev) => [...prev, t]);
    setNewTierName('');
  };
  const removeTierName = (name: string) => {
    if (
      !window.confirm(
        `Removing "${name}" from vocabulary. Existing member-calibre tiers using ` +
          'this name will be flagged as off-vocabulary. Continue?',
      )
    ) {
      return;
    }
    setTierNames((prev) => prev.filter((n) => n !== name));
  };

  // One-shot search seeds pushed into the typeahead pickers when an AI
  // suggestion is "used" (bumping `nonce` re-fires even for the same text).
  const [basePrefill, setBasePrefill] = useState<Prefill | null>(null);
  const [inspiredByPrefill, setInspiredByPrefill] = useState<Prefill | null>(null);
  const [competitorPrefill, setCompetitorPrefill] = useState<Prefill | null>(null);
  const nonceRef = useRef(0);
  const bumpNonce = () => (nonceRef.current += 1);

  const [saving, setSaving] = useState(false);
  const [localCalibres, setLocalCalibres] = useState<CustomCalibreEntry[]>(calibres);

  // When the chosen base calibre currently belongs to a DIFFERENT family, save is
  // held until the user confirms the move (setting it as base re-points its
  // singular `familyId` here, removing it from the other family). Holds the other
  // family's resolved name for the inline confirmation prompt.
  const [pendingBaseMove, setPendingBaseMove] = useState<
    { familyId: string; familyName: string } | null
  >(null);

  // AI suggestions (not written until applied per-field)
  const [aiLoading, setAiLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<SuggestionState | null>(null);
  // Fields the confidence gate suppressed because the AI was unsure and the
  // curator already had a value. Shown as a note so a held-back suggestion reads
  // as a decision rather than an empty response.
  const [heldBack, setHeldBack] = useState<string[]>([]);

  // Resolve friendly labels for the already-saved base calibre + inspired-by
  // reference on edit-open. Both fall back to a forced doc read when the value
  // isn't in the (possibly empty) local calibre list — the base-calibre picker
  // no longer depends on that list being populated.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (family?.baseCalibreId) {
          const local = localCalibres.find((c) => c.id === family.baseCalibreId);
          if (local) {
            if (!cancelled) setBaseCalibreLabel(local.name);
          } else {
            const cal = await getCustomCalibreById(family.baseCalibreId);
            if (!cancelled && cal) setBaseCalibreLabel(cal.name);
          }
        }
        const ib = family?.inspiredBy;
        if (ib?.type === 'family' && ib.familyId) {
          const fam = await getCalibreFamilyById(ib.familyId);
          if (!cancelled && fam) setInspiredByLabel(fam.displayName || fam.name);
        } else if (ib?.type === 'calibre' && ib.calibreId) {
          const local = localCalibres.find((c) => c.id === ib.calibreId);
          if (local) {
            if (!cancelled) setInspiredByLabel(local.name);
          } else {
            const cal = await getCustomCalibreById(ib.calibreId);
            if (!cancelled && cal) setInspiredByLabel(cal.name);
          }
        }
        // Resolve labels for any persisted functional competitors.
        const fc = family?.functionalCompetitor ?? [];
        if (fc.length) {
          const rows = await Promise.all(
            fc.map(async (entry): Promise<CompetitorRow> => {
              const fallback = entry.familyId ?? entry.calibreId ?? '';
              try {
                if (entry.type === 'family' && entry.familyId) {
                  const fam = await getCalibreFamilyById(entry.familyId);
                  return { entry, label: fam ? fam.displayName || fam.name : fallback };
                }
                if (entry.type === 'calibre' && entry.calibreId) {
                  const local = localCalibres.find((c) => c.id === entry.calibreId);
                  if (local) return { entry, label: local.name };
                  const cal = await getCustomCalibreById(entry.calibreId);
                  return { entry, label: cal ? cal.name : fallback };
                }
              } catch (err) {
                console.error('Failed to resolve competitor label', err);
              }
              return { entry, label: fallback };
            }),
          );
          if (!cancelled) setCompetitors(rows);
        }
      } catch (err) {
        console.error('Failed to resolve base/inspired-by label', err);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Only on initial mount for the passed-in family.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const members = useMemo(
    () => (isEdit ? localCalibres.filter((c) => c.familyId === savedId) : []),
    [localCalibres, savedId, isEdit],
  );

  // Current family (id + resolved name) of the calibre chosen as base, or null if
  // it isn't in any family. Prefers the locally-loaded list, falling back to a
  // forced doc read for base calibres linked via `additionalManufacturerIds`
  // (which aren't in the primary-manufacturer list).
  const resolveBaseCurrentFamily = async (
    calibreId: string,
  ): Promise<{ familyId: string; familyName: string } | null> => {
    const local = localCalibres.find((c) => c.id === calibreId);
    const cal = local ?? (await getCustomCalibreById(calibreId));
    const fid = cal?.familyId;
    if (!fid) return null;
    const fam = await getCalibreFamilyById(fid);
    return { familyId: fid, familyName: fam ? fam.displayName || fam.name : fid };
  };

  const handleSave = async (opts?: { baseMoveConfirmed?: boolean }) => {
    if (!name.trim()) {
      toast.error('Family name is required');
      return;
    }
    const wasNew = savedId === null;
    // The base calibre must be *made a member* when it's newly set or changed
    // (Fix 1). On a brand-new family there's no persisted base to compare against.
    const persistedBaseId = family?.baseCalibreId ?? '';
    const baseChanged = !!baseCalibreId && (wasNew || baseCalibreId !== persistedBaseId);

    // Before moving a base calibre out of another family, confirm with the user
    // (familyId is singular — setting it here removes it from that family).
    if (baseChanged && !opts?.baseMoveConfirmed) {
      try {
        const current = await resolveBaseCurrentFamily(baseCalibreId);
        if (current && current.familyId !== savedId) {
          setPendingBaseMove(current);
          return;
        }
      } catch (err) {
        console.error('[CalibreFamily] base family check failed', err);
      }
    }
    setPendingBaseMove(null);

    setSaving(true);
    try {
      const id = await saveCalibreFamily(
        {
          manufacturerId,
          name: name.trim(),
          displayName: displayName.trim(),
          description: description.trim() || undefined,
          baseCalibreId: baseCalibreId || undefined,
          inspiredBy: inspiredBy ?? undefined,
          functionalCompetitor: competitors.map((r) => r.entry),
          tierNames,
        },
        savedId ?? undefined,
        { claimBaseAsMember: baseChanged },
      );
      // Defensive: a save that resolves without an id would otherwise leave the
      // modal stuck on the create screen with no feedback (the original Bug 1
      // symptom). Treat it as a hard failure so the catch surfaces it.
      if (!id) throw new Error('Save returned no family id');

      // Promote to edit mode + show success BEFORE the parent reload, so a
      // reload failure can never swallow the success path or block the
      // transition to the members UI.
      setSavedId(id);
      if (wasNew) {
        toast.success('Family created — you can now add members below');
      } else {
        toast.success('Family saved');
      }

      // If the base calibre was (re)assigned as a member, refresh the modal's own
      // calibre list so the members section immediately reflects the new membership.
      if (baseChanged) {
        try {
          setLocalCalibres(await reloadCalibres());
        } catch (reloadErr) {
          console.error('Family saved, but reloading calibres failed', reloadErr);
        }
      }

      // Best-effort refresh of the parent list; its failure is logged but must
      // not look like (or undo) a successful save. Pass the new id so the parent
      // can re-point the open editor at the persisted family (durable edit mode).
      try {
        await onSaved(id);
      } catch (reloadErr) {
        console.error('Family saved, but reloading the list failed', reloadErr);
      }

      if (!wasNew) onClose();
    } catch (err) {
      // Surface the real backend cause (e.g. Firestore "Missing or insufficient
      // permissions") and keep the modal open with the form intact so the user
      // can retry once the cause is resolved — never close/reset on error.
      console.error('[CalibreFamily] save failed', err);
      toast.error(`Failed to save family: ${errorMessage(err)}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!savedId) return;
    if (
      !window.confirm(
        `Delete family "${name}"? Its ${members.length} member calibre${
          members.length === 1 ? '' : 's'
        } will be un-assigned (the calibres themselves are kept).`,
      )
    ) {
      return;
    }
    setSaving(true);
    try {
      await deleteCalibreFamily(
        savedId,
        members.map((m) => m.id),
      );
      await onSaved();
      toast.success('Family deleted');
      onClose();
    } catch (err) {
      console.error('[CalibreFamily] delete failed', err);
      toast.error(`Failed to delete family: ${errorMessage(err)}`);
    } finally {
      setSaving(false);
    }
  };

  // Assign several calibres to the family in one commit. `setCalibreFamilyMembership`
  // only takes one id, so the writes run sequentially (safest for Firestore); the
  // calibre list reloads once at the end. Partial failures still reload so the UI
  // reflects whatever did persist.
  const handleAddMembers = async (calibreIds: string[]) => {
    if (!savedId || calibreIds.length === 0) return;
    try {
      for (const cid of calibreIds) {
        await setCalibreFamilyMembership(cid, savedId);
      }
      toast.success(
        `Added ${calibreIds.length} member${calibreIds.length === 1 ? '' : 's'}`,
      );
    } catch (err) {
      console.error('[CalibreFamily] add members failed', err);
      toast.error(`Failed to add members: ${errorMessage(err)}`);
    } finally {
      setLocalCalibres(await reloadCalibres());
      await onSaved();
    }
  };

  const handleRemoveMember = async (calibreId: string) => {
    try {
      await setCalibreFamilyMembership(calibreId, null);
      setLocalCalibres(await reloadCalibres());
      await onSaved();
    } catch (err) {
      console.error('[CalibreFamily] remove member failed', err);
      toast.error(`Failed to remove member: ${errorMessage(err)}`);
    }
  };

  const handleAISuggest = async () => {
    if (!name.trim()) {
      toast.error('Enter a family name before asking for suggestions');
      return;
    }
    setAiLoading(true);
    setHeldBack([]);
    try {
      // Send the current draft as well as the family identity: without it the
      // model can't see what's already been researched, and re-proposes the same
      // generic guess over the curator's own values every run.
      const result: SuggestCalibreFamilyResult = await suggestCalibreFamily({
        manufacturerId,
        familyName: name.trim(),
        memberCalibreIds: members.map((m) => m.id),
        existingDescription: description.trim() || undefined,
        existingBaseCalibre: baseCalibreLabel.trim() || undefined,
        existingInspiredBy: inspiredByLabel.trim() || undefined,
        existingCompetitors: competitors
          .map((r) => r.label.trim())
          .filter((label) => label.length > 0),
      });
      const { next, heldBack } = gateSuggestions(result, {
        description,
        baseCalibreId,
        hasInspiredBy: inspiredBy !== null,
        competitorCount: competitors.length,
      });
      setHeldBack(heldBack);
      if (Object.keys(next).length === 0) {
        toast(
          heldBack.length > 0
            ? 'AI was unsure — your existing values were kept'
            : 'No suggestions returned',
        );
        setSuggestions(null);
        return;
      }
      if (next.functionalCompetitor?.length) setCompetitorsRevealed(true);
      setSuggestions(next);
    } catch (err) {
      console.error('[CalibreFamily] AI suggest failed', err);
      toast.error(`AI suggest failed: ${errorMessage(err)}`);
    } finally {
      setAiLoading(false);
    }
  };

  // Per-field apply / reject for AI suggestions.
  const applyDescription = () => {
    if (suggestions?.description) setDescription(suggestions.description.value);
    setSuggestions((s) => (s ? { ...s, description: undefined } : s));
  };
  const applyBaseCalibre = () => {
    const sug = suggestions?.baseCalibre;
    if (sug?.calibreId && localCalibres.some((c) => c.id === sug.calibreId)) {
      // The AI resolved a real id we already have loaded — apply it directly.
      const match = localCalibres.find((c) => c.id === sug.calibreId);
      setBaseCalibreId(sug.calibreId);
      if (match) setBaseCalibreLabel(match.name);
    } else if (sug?.name || sug?.calibreId) {
      // Otherwise seed the base-calibre typeahead with the AI's hint so the user
      // can pick from the (manufacturer-scoped, Algolia-backed) matches — the
      // broader pool, not just the locally-loaded list.
      setBasePrefill({ query: sug.name ?? sug.calibreId ?? '', nonce: bumpNonce() });
      toast('Search prefilled — pick the matching base calibre below');
    }
    setSuggestions((s) => (s ? { ...s, baseCalibre: undefined } : s));
  };
  const applyInspiredByHint = () => {
    const hint = suggestions?.inspiredBy?.hint;
    if (hint) {
      // Pre-fill the inspired-by typeahead with the AI's hint and auto-run the
      // search so matching references surface immediately.
      setInspiredByPrefill({ query: hint, nonce: bumpNonce() });
      toast('Search prefilled — pick the matching reference below');
    }
    setSuggestions((s) => (s ? { ...s, inspiredBy: undefined } : s));
  };
  const rejectField = (field: keyof SuggestionState) =>
    setSuggestions((s) => (s ? { ...s, [field]: undefined } : s));

  // Append competitor rows, deduped against what's already chosen.
  const addCompetitorRows = (rows: CompetitorRow[]) => {
    if (rows.length === 0) return;
    setCompetitors((prev) => {
      const seen = new Set(prev.map((r) => competitorKey(r.entry)));
      const merged = [...prev];
      for (const r of rows) {
        const k = competitorKey(r.entry);
        if (!seen.has(k)) {
          seen.add(k);
          merged.push(r);
        }
      }
      return merged;
    });
    setCompetitorsRevealed(true);
  };

  const removeCompetitor = (key: string) =>
    setCompetitors((prev) => prev.filter((r) => competitorKey(r.entry) !== key));

  // Resolve one AI competitor suggestion into a structured FK + label. ONLY an
  // id the callable itself resolved counts: we fetch that doc for its real
  // manufacturerId + canonical name, which also proves the entity exists.
  //
  // A free-text `name` is deliberately NOT searched here. The model happily
  // invents plausible rivals ("Calibre 324 S C", "Calibre 5800") that aren't in
  // the catalogue, and taking Algolia's top hit for one of those silently
  // attached a DIFFERENT calibre (324 S C → "Patek Philippe Calibre 335 S C").
  // Name-only suggestions return null so the caller prefills the typeahead and
  // the curator picks the real entity — same as inspired-by's hint.
  const resolveCompetitor = async (
    s: FunctionalCompetitorSuggestion,
  ): Promise<CompetitorRow | null> => {
    try {
      if (s.familyId) {
        const fam = await getCalibreFamilyById(s.familyId);
        if (fam) {
          return {
            entry: { type: 'family', familyId: fam.id, manufacturerId: fam.manufacturerId },
            label: fam.displayName || fam.name,
          };
        }
      }
      if (s.calibreId) {
        const cal = await getCustomCalibreById(s.calibreId);
        if (cal) {
          return {
            entry: {
              type: 'calibre',
              calibreId: cal.id,
              manufacturerId: cal.manufacturerId ?? '',
            },
            label: cal.name,
          };
        }
      }
    } catch (err) {
      console.error('[CalibreFamily] competitor id resolve failed', err);
    }
    return null;
  };

  const dropCompetitorSuggestion = (index: number) =>
    setSuggestions((cur) => {
      if (!cur?.functionalCompetitor) return cur;
      const next = cur.functionalCompetitor.filter((_, i) => i !== index);
      return { ...cur, functionalCompetitor: next.length ? next : undefined };
    });

  // Apply a single competitor suggestion: attach it only when the callable gave
  // us a real id, otherwise seed the picker with the AI's own words and let the
  // curator choose (or discover the calibre doesn't exist). Mirrors inspired-by's
  // hint. Either way the suggestion leaves the list.
  const applyCompetitor = async (s: FunctionalCompetitorSuggestion, index: number) => {
    const row = await resolveCompetitor(s);
    if (row) {
      addCompetitorRows([row]);
    } else {
      const query = [s.manufacturer, s.name].filter(Boolean).join(' ').trim();
      if (query) {
        setCompetitorPrefill({ query, nonce: bumpNonce() });
        setCompetitorsRevealed(true);
        toast('Search prefilled — pick the matching competitor below');
      } else {
        toast('Nothing to search for in that suggestion');
      }
    }
    dropCompetitorSuggestion(index);
  };

  // Apply every competitor suggestion at once. Only the ones the callable gave a
  // real id for are added; name-only suggestions stay in the list so the curator
  // can Add them one at a time and pick the entity from the typeahead.
  const applyAllCompetitors = async () => {
    const list = suggestions?.functionalCompetitor;
    if (!list?.length) return;
    const resolved = await Promise.all(list.map((s) => resolveCompetitor(s)));
    const rows = resolved.filter((r): r is CompetitorRow => r !== null);
    addCompetitorRows(rows);
    setSuggestions((cur) => {
      if (!cur?.functionalCompetitor) return cur;
      const remaining = cur.functionalCompetitor.filter((_, i) => resolved[i] === null);
      return { ...cur, functionalCompetitor: remaining.length ? remaining : undefined };
    });
    const unresolved = resolved.length - rows.length;
    if (rows.length && unresolved) {
      toast.success(`Added ${rows.length}; ${unresolved} need a manual pick`);
    } else if (rows.length) {
      toast.success(`Added ${rows.length} competitor${rows.length === 1 ? '' : 's'}`);
    } else {
      toast('None came with a resolved id — use Add on each to search for it');
    }
  };

  const hasSuggestions =
    !!suggestions &&
    (suggestions.description ||
      suggestions.baseCalibre ||
      suggestions.inspiredBy ||
      suggestions.functionalCompetitor?.length);

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={isEdit ? `Edit family: ${family?.name ?? name}` : 'New calibre family'}
      size="xl"
    >
      <div className="space-y-4">
        {/* Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. SW200"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
          />
          <p className="text-xs text-gray-500 mt-1">
            Unique per manufacturer. Slug:{' '}
            <span className="font-mono">{slugPreview(name)}</span>
          </p>
        </div>

        {/* Display name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Display name
          </label>
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder={name ? `${name} Family` : 'e.g. SW200 Family'}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
          />
          <p className="text-xs text-gray-500 mt-1">
            Defaults to “{name ? `${name} Family` : 'Name Family'}” if left empty.
          </p>
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
          />
        </div>

        {/* Tier vocabulary — canonical tier names member calibres draw from. */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Tier vocabulary
          </label>
          <p className="text-xs text-gray-500 mb-2">
            Canonical tier names for member calibres (e.g. Elaboré, Top,
            Chronometer). When set, the calibre tier editor offers these and
            flags off-vocabulary names. Leave empty for free-text tier names.
            This list still feeds the tier editor and AI enrichment today; the
            structured Calibre Tier Names below take over in a later phase.
          </p>
          {sortedTierNames.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-2">
              {sortedTierNames.map((n) => (
                <span
                  key={n}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-sm bg-brand-100 text-brand-800"
                >
                  {n}
                  <button
                    type="button"
                    onClick={() => removeTierName(n)}
                    className="hover:text-red-600"
                    title={`Remove "${n}"`}
                  >
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                </span>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <input
              type="text"
              value={newTierName}
              onChange={(e) => setNewTierName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addTierName();
                }
              }}
              placeholder="Add a tier name…"
              className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            />
            <button
              type="button"
              onClick={addTierName}
              disabled={!newTierName.trim()}
              className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Add
            </button>
          </div>
        </div>

        {/* Structured tier names save immediately (one transaction per change),
            so they need a family that already exists. */}
        {savedId && (
          <FamilyTierNamesSection
            manufacturerId={manufacturerId}
            family={{
              id: savedId,
              name: family?.name || name || 'This',
              tierNames: family?.tierNames,
              tierVocabulary: family?.tierVocabulary,
            }}
            families={families}
          />
        )}

        {/* Base calibre, inspired-by, AI suggestions and members all act on a
            persisted family (FK writes, member assignment, AI member context),
            so they only appear once the family exists. On create we show just
            the fields that persist cleanly up-front (name / display / desc). */}
        {isEdit ? (
          <>
            {/* Base calibre */}
            <BaseCalibrePicker
              manufacturerId={manufacturerId}
              value={baseCalibreId}
              label={baseCalibreLabel}
              prefill={basePrefill}
              onChange={(id, label) => {
                setBaseCalibreId(id);
                setBaseCalibreLabel(label);
                // A different base invalidates any pending cross-family move prompt.
                setPendingBaseMove(null);
              }}
            />

            {/* Inspired by */}
            <InspiredByPicker
              value={inspiredBy}
              label={inspiredByLabel}
              prefill={inspiredByPrefill}
              onChange={(ib, label) => {
                setInspiredBy(ib);
                setInspiredByLabel(label);
              }}
            />

            {/* Functional competitors — hidden for clean/empty families until
                there's content, an AI suggestion, or the user opts to add one. */}
            {competitors.length > 0 || competitorsRevealed ? (
              <FunctionalCompetitorsEditor
                rows={competitors}
                prefill={competitorPrefill}
                onAdd={(entry, label) => addCompetitorRows([{ entry, label }])}
                onRemove={removeCompetitor}
              />
            ) : (
              <button
                type="button"
                onClick={() => setCompetitorsRevealed(true)}
                className="text-sm text-brand-700 hover:text-brand-900 font-medium"
              >
                + Add functional competitors
              </button>
            )}

            {/* AI suggest */}
            <div className="border border-purple-100 bg-purple-50/40 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-purple-800">
                  AI suggestions
                </span>
                <Button
                  variant="secondary"
                  size="sm"
                  loading={aiLoading}
                  disabled={!name.trim()}
                  onClick={handleAISuggest}
                  title={
                    !name.trim() ? 'Enter a name first' : 'Ask Claude for suggestions'
                  }
                >
                  ✨ Suggest
                </Button>
              </div>
              {heldBack.length > 0 && (
                <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  AI unsure — kept your existing {heldBack.join(', ')}. Clear a
                  field and re-run if you want its suggestion anyway.
                </div>
              )}
              {hasSuggestions && (
                <div className="mt-3 space-y-2">
                  {suggestions?.description && (
                    <SuggestionRow
                      label="Description"
                      confidence={suggestions.description.confidence}
                      onApply={applyDescription}
                      onReject={() => rejectField('description')}
                    >
                      <span className="whitespace-pre-wrap break-words">
                        {suggestions.description.value}
                      </span>
                    </SuggestionRow>
                  )}
                  {suggestions?.baseCalibre && (
                    <SuggestionRow
                      label="Base calibre"
                      confidence={suggestions.baseCalibre.confidence}
                      applyLabel="Search ↑"
                      onApply={applyBaseCalibre}
                      onReject={() => rejectField('baseCalibre')}
                    >
                      {suggestions.baseCalibre.name ?? suggestions.baseCalibre.calibreId}
                    </SuggestionRow>
                  )}
                  {suggestions?.inspiredBy && (
                    <SuggestionRow
                      label="Inspired by (hint)"
                      confidence={suggestions.inspiredBy.confidence}
                      // The hint is a raw string — seed the inspired-by picker
                      // with it so matching references surface for selection.
                      applyLabel="Search ↑"
                      onApply={applyInspiredByHint}
                      onReject={() => rejectField('inspiredBy')}
                    >
                      {suggestions.inspiredBy.hint}
                    </SuggestionRow>
                  )}
                  {suggestions?.functionalCompetitor &&
                    suggestions.functionalCompetitor.length > 0 && (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                            Functional competitors
                          </span>
                          <button
                            type="button"
                            onClick={applyAllCompetitors}
                            className="text-xs text-brand-700 hover:text-brand-900 font-medium px-2 py-1 rounded hover:bg-brand-50"
                          >
                            Apply all
                          </button>
                        </div>
                        {suggestions.functionalCompetitor.map((s, i) => (
                          <SuggestionRow
                            // No stable id on free-text suggestions; index is fine
                            // since the list only shrinks (applied items drop out).
                            key={`fc-${i}`}
                            label="Competitor"
                            confidence={s.confidence}
                            applyLabel="Add"
                            onApply={() => applyCompetitor(s, i)}
                            onReject={() => dropCompetitorSuggestion(i)}
                          >
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium">{s.name ?? '(unnamed)'}</span>
                              {s.manufacturer && (
                                <span className="text-xs text-gray-500">
                                  {s.manufacturer}
                                </span>
                              )}
                            </div>
                            {s.reasoning && (
                              <p className="text-xs text-gray-500 mt-0.5">{s.reasoning}</p>
                            )}
                          </SuggestionRow>
                        ))}
                      </div>
                    )}
                </div>
              )}
            </div>

            {/* Members */}
            <MemberManager
              allCalibres={localCalibres}
              familyId={savedId}
              onAddMany={handleAddMembers}
              onRemove={handleRemoveMember}
            />
          </>
        ) : (
          <p className="text-xs text-gray-500 italic border-t pt-3">
            Save the family to unlock base calibre, inspired by, functional
            competitors, AI suggestions, and members.
          </p>
        )}

        {/* Cross-family base-move confirmation — the chosen base calibre already
            belongs to another family; setting it as base moves it here. */}
        {pendingBaseMove && (
          <div className="border border-amber-300 bg-amber-50 rounded-lg p-3">
            <p className="text-sm text-amber-800">
              This calibre is currently in family{' '}
              <span className="font-semibold">{pendingBaseMove.familyName}</span>. Setting
              it as base will move it here.
            </p>
            <div className="flex items-center gap-2 mt-2">
              <Button
                size="sm"
                onClick={() => handleSave({ baseMoveConfirmed: true })}
                loading={saving}
              >
                Move it here & save
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setPendingBaseMove(null)}
                disabled={saving}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex flex-wrap items-center justify-end gap-3 pt-4 border-t">
          {isEdit && (
            <Button
              variant="danger"
              onClick={handleDelete}
              disabled={saving}
              className="mr-auto"
            >
              Delete family
            </Button>
          )}
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            {isEdit ? 'Close' : 'Cancel'}
          </Button>
          <Button
            onClick={() => handleSave()}
            loading={saving}
            disabled={!name.trim()}
          >
            {isEdit ? 'Save' : 'Create'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function slugPreview(name: string): string {
  return (
    name
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '-')
      .replace(/^-|-$/g, '') || '—'
  );
}

// ---------------------------------------------------------------------------
// AI suggestion row
// ---------------------------------------------------------------------------

function SuggestionRow({
  label,
  confidence,
  children,
  onApply,
  onReject,
  applyLabel = 'Apply',
}: {
  label: string;
  confidence?: CalibreFamilyConfidence;
  children: React.ReactNode;
  onApply: () => void;
  onReject: () => void;
  applyLabel?: string;
}) {
  return (
    <div className="flex items-start gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
            {label}
          </span>
          <Badge variant={confidenceVariant(confidence)}>{confidence ?? 'medium'}</Badge>
        </div>
        <div className="text-gray-900">{children}</div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <button
          type="button"
          onClick={onApply}
          className="text-xs text-brand-700 hover:text-brand-900 font-medium px-2 py-1 rounded hover:bg-brand-50"
        >
          {applyLabel}
        </button>
        <button
          type="button"
          onClick={onReject}
          className="text-xs text-gray-400 hover:text-gray-600 px-2 py-1 rounded hover:bg-gray-50"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Member manager (edit mode)
// ---------------------------------------------------------------------------

/**
 * Membership state of a calibre relative to the family being edited. Calibres
 * already in a DIFFERENT family are hard-excluded from the picker entirely (see
 * `rows`), so only these two states ever reach the list.
 */
type MemberRowState = 'member' | 'free';

function MemberManager({
  allCalibres,
  familyId,
  onAddMany,
  onRemove,
}: {
  allCalibres: CustomCalibreEntry[];
  familyId: string | null;
  onAddMany: (calibreIds: string[]) => Promise<void>;
  onRemove: (calibreId: string) => void;
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState('');
  // Checked-but-not-yet-committed calibre ids. Survives search changes so the
  // user can tick across multiple filtered views before committing in one go.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [adding, setAdding] = useState(false);

  const members = useMemo(
    () => allCalibres.filter((c) => !!familyId && c.familyId === familyId),
    [allCalibres, familyId],
  );

  // The picker HARD-EXCLUDES any calibre already assigned to a DIFFERENT family:
  // a calibre can only be in one family (`familyId` is singular), so cross-family
  // candidates are never selectable and must be freed from that family first.
  // Only unassigned calibres (`familyId` empty) and this family's own members
  // remain — members show checked+disabled, the rest are tickable.
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allCalibres
      .filter((c) => (q ? c.name.toLowerCase().includes(q) : true))
      .filter((c) => !c.familyId || c.familyId === familyId)
      .map((c) => {
        const state: MemberRowState =
          !!familyId && c.familyId === familyId ? 'member' : 'free';
        return { calibre: c, state };
      });
  }, [allCalibres, search, familyId]);

  const freeCount = useMemo(() => rows.filter((r) => r.state === 'free').length, [rows]);

  const closePicker = () => {
    setPickerOpen(false);
    setSearch('');
    setSelected(new Set());
  };

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const commit = async () => {
    if (selected.size === 0) return;
    setAdding(true);
    try {
      await onAddMany([...selected]);
      closePicker();
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="border-t pt-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-gray-700">
          Members ({members.length})
        </span>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => (pickerOpen ? closePicker() : setPickerOpen(true))}
        >
          {pickerOpen ? 'Done' : '+ Add members'}
        </Button>
      </div>

      {members.length === 0 ? (
        <p className="text-xs text-gray-400 italic">No member calibres assigned yet.</p>
      ) : (
        <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg">
          {members.map((c) => (
            <li
              key={c.id}
              className="flex items-center justify-between px-3 py-1.5 text-sm"
            >
              <span className="text-gray-800">{c.name}</span>
              <button
                type="button"
                onClick={() => onRemove(c.id)}
                className="text-xs text-red-600 hover:text-red-800 underline"
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}

      {pickerOpen && (
        <div className="mt-2 border border-gray-200 rounded-lg p-2">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search this manufacturer's calibres…"
            className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500 mb-1.5"
          />
          <div className="flex items-center justify-end mb-1.5 px-0.5">
            <span className="text-xs text-gray-400">
              {freeCount} unassigned · {rows.length} shown
            </span>
          </div>
          <div className="max-h-72 overflow-y-auto">
            {rows.length === 0 ? (
              <div className="text-xs text-gray-400 p-2">
                {allCalibres.length === 0
                  ? 'No calibres for this manufacturer.'
                  : 'No matches.'}
              </div>
            ) : (
              <ul className="divide-y divide-gray-100">
                {rows.map(({ calibre: c, state }) => {
                  const disabled = state !== 'free' || adding;
                  const checked = state === 'member' || selected.has(c.id);
                  return (
                    <li key={c.id}>
                      <label
                        className={`flex items-center gap-2 px-2 py-1.5 rounded ${
                          state !== 'free'
                            ? 'opacity-60 cursor-not-allowed'
                            : selected.has(c.id)
                              ? 'bg-brand-50/50 cursor-pointer'
                              : 'hover:bg-gray-50 cursor-pointer'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggle(c.id)}
                          disabled={disabled}
                          className="rounded text-brand-600 focus:ring-brand-500"
                        />
                        <span className="text-sm text-gray-800 flex-1">{c.name}</span>
                        {state === 'member' && (
                          <Badge variant="success">member</Badge>
                        )}
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          <p className="text-xs text-gray-400 mt-1.5 px-0.5">
            Calibres already assigned to another family are hidden. Remove them from
            that family first to reassign.
          </p>
          <div className="flex items-center justify-end gap-2 mt-2 pt-2 border-t border-gray-100">
            <Button variant="ghost" size="sm" onClick={closePicker} disabled={adding}>
              Done
            </Button>
            <Button
              size="sm"
              onClick={commit}
              loading={adding}
              disabled={selected.size === 0}
            >
              Add {selected.size} selected
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared typeahead affordances
// ---------------------------------------------------------------------------

/** Leading magnifying-glass adornment so a typeahead reads as a search box. */
function SearchIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="2" />
      <path
        d="m17 17-3.6-3.6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

/** Small inline spinner shown while a typeahead search is in flight. */
function PickerSpinner() {
  return (
    <svg
      className="animate-spin h-4 w-4 text-brand-500"
      viewBox="0 0 24 24"
      fill="none"
      aria-label="Searching"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Inspired-by picker (search-as-you-type across families + calibres)
// ---------------------------------------------------------------------------

function InspiredByPicker({
  value,
  label,
  prefill,
  onChange,
}: {
  value: CalibreFamilyInspiredBy | null;
  label: string;
  prefill?: Prefill | null;
  onChange: (value: CalibreFamilyInspiredBy | null, label: string) => void;
}) {
  const [search, setSearch] = useState('');
  const [hits, setHits] = useState<InspiredByRefHit[]>([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Seed the search box from an external prefill (e.g. an applied AI hint). The
  // debounced search effect below then runs it automatically. `nonce` keys the
  // effect so the same query can be pushed again.
  useEffect(() => {
    if (prefill) setSearch(prefill.query);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill?.nonce]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = search.trim();
    if (q.length < 2) {
      setHits([]);
      setSearching(false);
      setOpen(false);
      return;
    }
    setSearching(true);
    debounceRef.current = setTimeout(() => {
      searchInspiredByRefs(q)
        .then((res) => {
          setHits(res);
          setOpen(true);
        })
        .catch((err) => {
          console.error('Inspired-by search failed', err);
          setHits([]);
        })
        .finally(() => setSearching(false));
    }, 250);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [search]);

  const select = (hit: InspiredByRefHit) => {
    const ib: CalibreFamilyInspiredBy =
      hit.kind === 'family'
        ? { type: 'family', familyId: hit.objectID, manufacturerId: hit.manufacturerId ?? '' }
        : { type: 'calibre', calibreId: hit.objectID, manufacturerId: hit.manufacturerId ?? '' };
    onChange(ib, hit.name);
    setSearch('');
    setHits([]);
    setOpen(false);
  };

  const showEmpty = open && !searching && hits.length === 0 && search.trim().length >= 2;

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Inspired by <span className="font-normal text-gray-400">(search)</span>
      </label>
      {value ? (
        <div className="flex items-center gap-2 mb-2">
          <span className="inline-flex items-center gap-1 bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-full pl-3 pr-1.5 py-1">
            <span className="text-xs uppercase tracking-wide text-amber-600">
              {value.type}
            </span>
            <span className="font-medium">
              {label || value.familyId || value.calibreId}
            </span>
            <button
              type="button"
              onClick={() => onChange(null, '')}
              aria-label="Clear inspired-by"
              className="rounded-full hover:bg-amber-100 w-5 h-5 inline-flex items-center justify-center text-amber-700"
            >
              ×
            </button>
          </span>
        </div>
      ) : null}

      <div className="relative">
        <SearchIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onFocus={() => (hits.length > 0 || showEmpty) && setOpen(true)}
          placeholder="Search families or calibres (e.g. ETA 2824)…"
          className="w-full border border-gray-300 rounded-lg pl-9 pr-9 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
        />
        {searching && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2">
            <PickerSpinner />
          </span>
        )}
        {open && hits.length > 0 && (
          <ul className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
            {hits.map((h) => (
              <li key={`${h.kind}-${h.objectID}`}>
                <button
                  type="button"
                  onClick={() => select(h)}
                  className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-2"
                >
                  <Badge variant={h.kind === 'family' ? 'info' : 'default'}>
                    {h.kind}
                  </Badge>
                  <span className="text-sm text-gray-800 flex-1">{h.name}</span>
                  {h.manufacturerId && (
                    <code className="text-xs text-gray-400">{h.manufacturerId}</code>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
        {showEmpty && (
          <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-sm text-gray-500">
            No matching family or calibre found.
          </div>
        )}
      </div>
      <p className="text-xs text-gray-500 mt-1">
        Start typing to search families and calibres across all manufacturers — e.g. a
        Sellita family inspired by an ETA calibre. Optional.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Functional-competitors editor (chips + add-typeahead, multi-value)
// ---------------------------------------------------------------------------

/**
 * Edits a family's functional-competitor list. Each chosen entry renders as a
 * chip (resolved name + manufacturer FK) with a remove ×; the typeahead below
 * (same `searchInspiredByRefs` index the inspired-by picker uses) adds more.
 * Mirrors inspired-by's layout and spacing but in sky/blue to read as "competes
 * with" (market positioning) rather than amber "inspired by" (lineage), and is
 * multi-value since a family usually competes with several rivals.
 */
function FunctionalCompetitorsEditor({
  rows,
  prefill,
  onAdd,
  onRemove,
}: {
  rows: CompetitorRow[];
  prefill?: Prefill | null;
  onAdd: (value: CalibreFamilyFunctionalCompetitor, label: string) => void;
  onRemove: (key: string) => void;
}) {
  const [search, setSearch] = useState('');
  const [hits, setHits] = useState<InspiredByRefHit[]>([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Seed the search box from an external prefill (an AI suggestion with no
  // confident auto-match). The debounced effect below then runs it.
  useEffect(() => {
    if (prefill) setSearch(prefill.query);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill?.nonce]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = search.trim();
    if (q.length < 2) {
      setHits([]);
      setSearching(false);
      setOpen(false);
      return;
    }
    setSearching(true);
    debounceRef.current = setTimeout(() => {
      searchInspiredByRefs(q)
        .then((res) => {
          setHits(res);
          setOpen(true);
        })
        .catch((err) => {
          console.error('Competitor search failed', err);
          setHits([]);
        })
        .finally(() => setSearching(false));
    }, 250);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [search]);

  const chosen = new Set(rows.map((r) => competitorKey(r.entry)));

  const select = (hit: InspiredByRefHit) => {
    const entry: CalibreFamilyFunctionalCompetitor =
      hit.kind === 'family'
        ? { type: 'family', familyId: hit.objectID, manufacturerId: hit.manufacturerId ?? '' }
        : { type: 'calibre', calibreId: hit.objectID, manufacturerId: hit.manufacturerId ?? '' };
    onAdd(entry, hit.name);
    setSearch('');
    setHits([]);
    setOpen(false);
  };

  const showEmpty = open && !searching && hits.length === 0 && search.trim().length >= 2;

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Functional competitors <span className="font-normal text-gray-400">(search)</span>
      </label>
      {rows.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap mb-2">
          {rows.map((r) => {
            const key = competitorKey(r.entry);
            return (
              <span
                key={key}
                className="inline-flex items-center gap-1 bg-sky-50 border border-sky-200 text-sky-800 text-sm rounded-full pl-3 pr-1.5 py-1"
              >
                <span className="text-xs uppercase tracking-wide text-sky-600">
                  {r.entry.type}
                </span>
                <span className="font-medium">
                  {r.label || r.entry.familyId || r.entry.calibreId}
                </span>
                <button
                  type="button"
                  onClick={() => onRemove(key)}
                  aria-label="Remove competitor"
                  className="rounded-full hover:bg-sky-100 w-5 h-5 inline-flex items-center justify-center text-sky-700"
                >
                  ×
                </button>
              </span>
            );
          })}
        </div>
      )}

      <div className="relative">
        <SearchIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onFocus={() => (hits.length > 0 || showEmpty) && setOpen(true)}
          placeholder="Search families or calibres to add (e.g. ETA 2824)…"
          className="w-full border border-gray-300 rounded-lg pl-9 pr-9 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
        />
        {searching && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2">
            <PickerSpinner />
          </span>
        )}
        {open && hits.length > 0 && (
          <ul className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
            {hits.map((h) => {
              const already = chosen.has(`${h.kind}:${h.objectID}`);
              return (
                <li key={`${h.kind}-${h.objectID}`}>
                  <button
                    type="button"
                    disabled={already}
                    onClick={() => select(h)}
                    className={`w-full text-left px-3 py-2 flex items-center gap-2 ${
                      already ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'
                    }`}
                  >
                    <Badge variant={h.kind === 'family' ? 'info' : 'default'}>{h.kind}</Badge>
                    <span className="text-sm text-gray-800 flex-1">{h.name}</span>
                    {already ? (
                      <span className="text-xs text-gray-400">added</span>
                    ) : (
                      h.manufacturerId && (
                        <code className="text-xs text-gray-400">{h.manufacturerId}</code>
                      )
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
        {showEmpty && (
          <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-sm text-gray-500">
            No matching family or calibre found.
          </div>
        )}
      </div>
      <p className="text-xs text-gray-500 mt-1">
        Families this one competes with on tier / use case / spec — market positioning,
        not lineage. Add several. Optional.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Base-calibre picker (Algolia typeahead scoped to this manufacturer)
// ---------------------------------------------------------------------------

/**
 * Search-as-you-type picker for the family's base calibre. Backed by Algolia
 * (`searchManufacturerCalibres`) rather than a flat `<select>` so it (a) scales
 * past the hundreds of calibres a big manufacturer like Sellita has, and (b)
 * matches calibres linked via `additionalManufacturerIds`, not just the primary
 * `manufacturerId`. Crucially it doesn't depend on the Firestore
 * `manufacturerId ==` list, which can come back empty (the original Bug 2).
 */
function BaseCalibrePicker({
  manufacturerId,
  value,
  label,
  prefill,
  onChange,
}: {
  manufacturerId: string;
  value: string;
  label: string;
  prefill?: Prefill | null;
  onChange: (calibreId: string, label: string) => void;
}) {
  const [search, setSearch] = useState('');
  const [hits, setHits] = useState<ManufacturerCalibreHit[]>([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Seed the search box from an external prefill (e.g. an applied AI base-calibre
  // suggestion). `nonce` keys the effect so the same query can be pushed again.
  useEffect(() => {
    if (prefill) setSearch(prefill.query);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill?.nonce]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = search.trim();
    if (q.length < 2) {
      setHits([]);
      setSearching(false);
      setOpen(false);
      return;
    }
    setSearching(true);
    debounceRef.current = setTimeout(() => {
      searchManufacturerCalibres(q, manufacturerId)
        .then((res) => {
          setHits(res);
          setOpen(true);
        })
        .catch((err) => {
          console.error('Base-calibre search failed', err);
          setHits([]);
        })
        .finally(() => setSearching(false));
    }, 250);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [search, manufacturerId]);

  const select = (hit: ManufacturerCalibreHit) => {
    onChange(hit.objectID, hit.name);
    setSearch('');
    setHits([]);
    setOpen(false);
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Base calibre <span className="font-normal text-gray-400">(search)</span>
      </label>
      {value ? (
        <div className="flex items-center gap-2 mb-2">
          <span className="inline-flex items-center gap-1 bg-gray-100 border border-gray-200 text-gray-800 text-sm rounded-full pl-3 pr-1.5 py-1">
            <span className="font-medium">{label || value}</span>
            <button
              type="button"
              onClick={() => onChange('', '')}
              aria-label="Clear base calibre"
              className="rounded-full hover:bg-gray-200 w-5 h-5 inline-flex items-center justify-center text-gray-600"
            >
              ×
            </button>
          </span>
        </div>
      ) : null}

      <div className="relative">
        <SearchIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onFocus={() => hits.length > 0 && setOpen(true)}
          placeholder="Search this manufacturer's calibres (e.g. SW200)…"
          className="w-full border border-gray-300 rounded-lg pl-9 pr-9 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
        />
        {searching && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2">
            <PickerSpinner />
          </span>
        )}
        {open && (
          <ul className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
            {hits.length === 0 ? (
              <li className="px-3 py-2 text-sm text-gray-400">
                No matching calibres for this manufacturer.
              </li>
            ) : (
              hits.map((h) => (
                <li key={h.objectID}>
                  <button
                    type="button"
                    onClick={() => select(h)}
                    className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-2"
                  >
                    <span className="text-sm text-gray-800 flex-1">{h.name}</span>
                  </button>
                </li>
              ))
            )}
          </ul>
        )}
      </div>
      <p className="text-xs text-gray-500 mt-1">
        The family&apos;s structural root (e.g. SW200-1). This manufacturer&apos;s
        calibres only. Optional.
      </p>
    </div>
  );
}
